import React, { Component, forwardRef } from "react";
import { Link as RouterLink, Redirect } from "react-router-dom";
import { connect } from "react-redux";
import DocumentTitle from "react-document-title";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Input,
  Button,
  withStyles,
  Link,
  Typography,
  InputAdornment,
  IconButton,
  DialogActions,
  DialogContent,
  DialogContentText
} from "@material-ui/core";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import GenericApi from "Api";
import { signIn, resetSignIn, acceptEula } from "./actions";
import { retrieveSignInData } from "./selectors";
import { downloadPDF } from "./utils";
import { withErrorBoundary } from "BaseApp/ErrorBoundary/ErrorBoundary";
import UnselfishDialog from "components/UnselfishDialog/UnselfishDialog";
import AuthLayout from "./AuthLayout";

const requiredFieldText = "This field is required";

const signInSchema = Yup.object().shape({
  username: Yup.string().required(requiredFieldText),
  password: Yup.string().required(requiredFieldText)
});

const mapState = state => ({
  signInData: retrieveSignInData(state)
});

const mapDispatch = dispatch => ({
  signIn: (username, password) => dispatch(signIn(username, password)),
  resetSignIn: () => dispatch(resetSignIn()),
  acceptEulaAction: (retryToken, agreement) =>
    dispatch(acceptEula(retryToken, agreement))
});

const styles = {
  field: {
    marginTop: "5px",
    width: "100%"
  },
  validationMessage: {
    height: "25px",
    padding: "3px 0"
  },
  alignTextCenter: {
    textAlign: "center"
  },
  bottom: {
    marginTop: "20px"
  }
};

const AdapterLink = forwardRef((props, ref) => (
  <RouterLink innerRef={ref} {...props} />
));

/**
 * A component to allow the users to login. When the login
 * is complete, the user should be able to access the application
 */
export class Login extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showPassword: false,
      username: "",
      showEulaDialog: false,
      retryToken: null,
      agreementPDFBlobUrl: null,
      agreementPDFfile: null
    };
  }

  async componentDidUpdate(prevProps) {
    const { signInData } = this.props;
    const { error, data } = signInData || {};
    const { detail } = (data && data.data) || {};
    const userEulaNotAccepted = detail === "Please accept current EULA";

    if (error && userEulaNotAccepted && !this.state.showEulaDialog) {
      const { retry_token, user_data } = data.data;
      this.setState({
        showEulaDialog: true,
        retryToken: retry_token
      });
      const response = await fetch(
        `${GenericApi.getBaseUrl()}/user/generate_agreement/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify(user_data)
        }
      );

      if (!response.ok) {
        const errors = await response.json();
        console.error("PDF generation failed:", errors);
        return;
      }

      const responseBlob = await response.blob();
      const agreementPDFfile = new File([responseBlob], "agreement.pdf");
      const agreementPDFBlobUrl = URL.createObjectURL(responseBlob);
      this.setState({
        agreementPDFBlobUrl: agreementPDFBlobUrl,
        agreementPDFfile: agreementPDFfile
      });
    }
  }

  componentWillUnmount = () => {
    this.props.resetSignIn();
    if (this.state.agreementPDFBlobUrl) {
      URL.revokeObjectURL(this.state.agreementPDFBlobUrl);
    }
  };

  togglePasswordVisibility = () => {
    this.setState(prevState => ({ showPassword: !prevState.showPassword }));
  };

  handleClose = () => {
    this.props.resetSignIn();
  };

  handleEulaClose = () => {
    this.props.resetSignIn();
    this.setState({ showEulaDialog: false });
  };

  handleEulaAccept = async () => {
    const { retryToken } = this.state;
    this.setState({ showEulaDialog: false, eulaAccepted: true });
    await this.props.acceptEulaAction(retryToken, this.state.agreementPDFfile);
  };

  render = () => {
    const { signIn, signInData, classes } = this.props,
      {
        showPassword,
        showEulaDialog,
        agreementPDFBlobUrl,
        username
      } = this.state,
      { error, data } = signInData || {},
      { detail } = (data && data.data) || {},
      userIsNotActive = detail === "User is not active",
      userLicenseIsExpired = detail === "User license is expired";
    const splitDetail = (detail && detail.split(" - ")) || [];

    return signInData && signInData.success ? (
      <Redirect to="/" />
    ) : (
      <DocumentTitle title="Login">
        <AuthLayout title="Login">
          <div className={`${classes.alignTextCenter}`}>
            {error && !userIsNotActive && splitDetail.length === 1 && detail}
            {error && !userIsNotActive && splitDetail.length === 2 && (
              <>
                <span>{splitDetail[0]}</span>
                <div style={{ width: 100, margin: "auto" }}>
                  <CountdownCircleTimer
                    isPlaying
                    duration={parseInt(splitDetail[1])}
                    size={100}
                    strokeWidth={6}
                    colors={[["#3f51b5"]]}
                  >
                    {({ remainingTime }) => remainingTime}
                  </CountdownCircleTimer>
                </div>
              </>
            )}
          </div>

          {showEulaDialog && (
            <UnselfishDialog
              onClose={this.handleEulaClose}
              open={showEulaDialog}
              maxWidth={"lg"}
              fullWidth
              PaperProps={{ style: { height: "80vh", maxHeight: "80vh" } }}
            >
              <DialogContent>
                <DialogContentText style={{ height: "95%" }}>
                  <Typography
                    variant="subtitle1"
                    style={{ marginBottom: "20px" }}
                  >
                    We have updated our End User License Agreement. Please
                    accept before proceeding.
                  </Typography>
                  <div style={{ height: "80%" }}>
                    <iframe
                      test-data="document_frame"
                      src={agreementPDFBlobUrl}
                      frameborder="1"
                      height="100%"
                      width="100%"
                    ></iframe>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        test-data="downloadAgreementButton"
                        onClick={() => downloadPDF(agreementPDFBlobUrl)}
                      >
                        Download Agreement
                      </Button>
                    </div>
                  </div>
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={this.handleEulaClose} color="primary">
                  Close
                </Button>
                <Button
                  onClick={this.handleEulaAccept}
                  variant="contained"
                  color="primary"
                  autoFocus
                >
                  Accept
                </Button>
              </DialogActions>
            </UnselfishDialog>
          )}

          <UnselfishDialog
            onClose={this.handleClose}
            open={error && userIsNotActive}
          >
            <DialogContent>
              <DialogContentText>
                The user <b>{username}</b> is not activated yet. The account
                owner shall receive a notification once the account has been
                activated. If your account was not activated within 5 working
                days please contact
                <Link href="mailto:info@planopsim.com">
                  {" "}
                  info@planopsim.com
                </Link>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={this.handleClose} color="primary" autoFocus>
                Close
              </Button>
            </DialogActions>
          </UnselfishDialog>

          <UnselfishDialog
            onClose={this.handleClose}
            open={error && userLicenseIsExpired}
            test-data="expiredUserDialog"
          >
            <DialogContent>
              <DialogContentText>
                Dear <b>{username}</b>, it appears that your license is expired,
                please contact support for renewal or for more information.
                <Link href="mailto:info@planopsim.com">
                  {" "}
                  info@planopsim.com
                </Link>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={this.handleClose} color="primary" autoFocus>
                Close
              </Button>
            </DialogActions>
          </UnselfishDialog>

          <Formik
            initialValues={{ username: "", password: "" }}
            validationSchema={signInSchema}
            onSubmit={values => {
              const { username, password } = values;
              this.setState({ username });
              signIn(username, password);
            }}
          >
            {({ errors, values, handleChange, handleBlur, touched }) => (
              <Form>
                <Input
                  className={classes.field}
                  name="username"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.username}
                  placeholder="Username *"
                />
                <div className={classes.validationMessage}>
                  {touched.username && errors.username}
                </div>

                <Input
                  className={classes.field}
                  type={showPassword ? "text" : "password"}
                  name="password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.password}
                  placeholder="Password *"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={this.togglePasswordVisibility}
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
                <div className={classes.validationMessage}>
                  {touched.password && errors.password}
                </div>

                <Button
                  name="submitLogin"
                  test-data="submitLogin"
                  className={classes.field}
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={signInData && signInData.loading}
                >
                  Submit
                </Button>

                <div className={`${classes.bottom} ${classes.alignTextCenter}`}>
                  <Link component={AdapterLink} to="/register">
                    <Typography test-data="createAccountLink">
                      Create an account
                    </Typography>
                  </Link>
                </div>
                <div className={`${classes.bottom} ${classes.alignTextCenter}`}>
                  <Link component={AdapterLink} to="/reset-password-request">
                    <Typography>Reset password</Typography>
                  </Link>
                </div>
              </Form>
            )}
          </Formik>
        </AuthLayout>
      </DocumentTitle>
    );
  };
}

export default connect(
  mapState,
  mapDispatch
)(withErrorBoundary(withStyles(styles)(Login)));
